import type { FieldDefinition } from '../data/model';
import { isPopupField, popupFields } from './fields';
import type { LayerPopupConfiguration, PopupFieldConfiguration } from './model';

export const MAX_POPUP_FIELD_LABEL_LENGTH = 512;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasExactKeys(value: Record<string, unknown>, keys: readonly string[]): boolean {
  const actual = Object.keys(value);
  return (
    actual.length === keys.length &&
    actual.every((key) => keys.includes(key)) &&
    keys.every((key) => Object.hasOwn(value, key))
  );
}

export function normalisePopupFieldLabel(label: string, field: string): string {
  return label.trim().length === 0 ? field : label;
}

function isPopupFieldConfigurationValid(
  value: unknown,
  fields: readonly FieldDefinition[],
): value is PopupFieldConfiguration {
  return (
    isRecord(value) &&
    hasExactKeys(value, ['field', 'label', 'visible']) &&
    typeof value.field === 'string' &&
    isPopupField(fields, value.field) &&
    typeof value.label === 'string' &&
    value.label.trim().length > 0 &&
    value.label.length <= MAX_POPUP_FIELD_LABEL_LENGTH &&
    typeof value.visible === 'boolean'
  );
}

export function isLayerPopupConfigurationValid(
  value: unknown,
  fields: readonly FieldDefinition[],
): value is LayerPopupConfiguration {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ['enabled', 'titleField', 'fields']) ||
    typeof value.enabled !== 'boolean' ||
    (value.titleField !== null && typeof value.titleField !== 'string') ||
    (typeof value.titleField === 'string' && !isPopupField(fields, value.titleField)) ||
    !Array.isArray(value.fields) ||
    value.fields.length > popupFields(fields).length ||
    !value.fields.every((field) => isPopupFieldConfigurationValid(field, fields))
  ) {
    return false;
  }

  const configuredFields = value.fields.map((field) => field.field);
  return new Set(configuredFields).size === configuredFields.length;
}

export function popupConfigurationHasContent(configuration: LayerPopupConfiguration): boolean {
  return configuration.titleField !== null || configuration.fields.some((field) => field.visible);
}
